import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { CustomerRegistrationForm } from '../models/customerModel';
import { useNavigate } from 'react-router-dom';
import useUserCountry from '../../../hooks/useUserCountry';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useProperList from '../../../hooks/useProperList';
import useAuth from '../../../hooks/useAuth';
import {
  createCustomerEndpoint,
} from '../services/customerService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getAreaCodesEndpoint } from '../../../services/landlordService';

export interface DocumentOption {
  value: string; // code interno, ej: "DCSVDUI"
  label: string; // nombre visible, ej: "DUI"
}

const useCustomer = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const { getPhoneCountryConfiguration, getCurrentCountry, getServiceUrlCountry } = useUserCountry();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { propersOptions } = useProperList();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const phone_config = getPhoneCountryConfiguration();

  // Carga de códigos de área
  const [areaCodes, setAreaCodes] = useState<any[]>([]);
  const loadAreaCodes = useCallback(async () => {
    try {
      const { data } = await callEndpoint(getAreaCodesEndpoint(token));
      setAreaCodes(Array.isArray(data.data) ? data['data'] : []);
    } catch {
      setAreaCodes([]);
    }
  }, [callEndpoint, token]);

  // Carga de tipos de documento por país
  const [documentOptions, setDocumentOptions] = useState<DocumentOption[]>([]);
  const loadDocuments = useCallback(async () => {
    try {
      const country = getCurrentCountry();
      const countryCode = country?.code || 'SV';
      const urlService = getServiceUrlCountry();
      const response = await axios.get(
        `${urlService}/user/v1/documents?countryCode=${countryCode}`,
      );
      const documents = response?.data?.data?.documents || [];
      const options: DocumentOption[] = documents.map((doc: any) => ({
        value: doc.code,
        label: doc.document,
      }));
      setDocumentOptions(options);
    } catch {
      setDocumentOptions([]);
    }
  }, []);

  useEffect(() => {
    loadAreaCodes();
    loadDocuments();
  }, [loadAreaCodes, loadDocuments]);

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    resetField,
  } = useForm<CustomerRegistrationForm>({
    resolver: yupResolver(
      yup
        .object({
          first_name: yup.string().required('Nombre es requerido'),
          last_name: yup.string().required('Apellido es requerido'),
          email: yup
            .string()
            .email('Ingrese un formato valido para correo')
            .required('Correo electrónico es requerido'),
          phone: yup.string().required('Teléfono es requerido'),
          proper_id: yup.string(),
          document_number: yup.string(),
          document_type: yup.string(),
        })
        .required(),
    ),
  });

  const doCreate = async (form: CustomerRegistrationForm) => {
    setError('');
    form.username = form.email;
    form.area_code = form.area_code;
    if (form.proper_id === undefined) delete form.proper_id;
    if (
      !form.document_number ||
      form.document_number.trim().length === 0 ||
      !form.document_type
    ) {
      delete form.document_number;
      delete form.document_type;
    }

    const { status, data } = await callEndpoint(
      createCustomerEndpoint(form, token),
    );
    if (status === 201) {
      return navigate('/tenant/view');
    }
    const response = fetchResponseAdapter(data);
    setError(response.message);
  };

  return {
    handleSubmit,
    doCreate,
    error,
    control,
    errors,
    propersOptions,
    setValue,
    isLoading,
    phone_config,
    areaCodes,
    loadAreaCodes,
    resetField,
    documentOptions,
  };
};

export default useCustomer;
